import React from 'react';
import {
  FileText,
  CreditCard,
  ShieldCheck,
  Award,
  Receipt,
  Building,
  FileCheck,
  Briefcase,
  Layers,
  Globe,
  FileCode,
  Sparkles,
  Palette,
  Shapes,
  Star,
  CheckCircle2,
  ChevronRight,
  Zap,
  Tag,
  Activity
} from 'lucide-react';
import { MarqueeBadge, MarqueeConfig } from '../types';

interface CategoryMarqueeSectionProps {
  config: MarqueeConfig;
  darkMode?: boolean;
  onSelectCategory?: (category: string) => void;
}

// Icon helper to render Lucide icon dynamically based on iconName string
const renderBadgeIcon = (iconName: string, glowColor: string = '#38bdf8') => {
  const props = { className: 'w-[14px] h-[14px] flex-shrink-0 transition-transform duration-300 group-hover/pill:scale-105', style: { color: glowColor } };
  
  switch (iconName?.toLowerCase()) {
    case 'filetext':
    case 'passport':
      return <FileText {...props} />;
    case 'creditcard':
    case 'id card':
    case 'idcard':
      return <CreditCard {...props} />;
    case 'shieldcheck':
    case 'driving license':
    case 'license':
      return <ShieldCheck {...props} />;
    case 'award':
    case 'certificate':
      return <Award {...props} />;
    case 'receipt':
    case 'utility bill':
      return <Receipt {...props} />;
    case 'building':
    case 'bank statement':
      return <Building {...props} />;
    case 'filecheck':
    case 'resume':
      return <FileCheck {...props} />;
    case 'activity':
    case 'health id':
      return <Activity {...props} />;
    case 'briefcase':
    case 'business documents':
      return <Briefcase {...props} />;
    case 'layers':
    case 'templates':
      return <Layers {...props} />;
    case 'globe':
    case 'visa documents':
      return <Globe {...props} />;
    case 'filecode':
    case 'editable psd':
    case 'psd':
      return <FileCode {...props} />;
    case 'sparkles':
    case 'ai templates':
      return <Sparkles {...props} />;
    case 'palette':
    case 'cdr files':
      return <Palette {...props} />;
    case 'shapes':
    case 'vector files':
      return <Shapes {...props} />;
    case 'star':
    case 'premium resources':
      return <Star {...props} />;
    default:
      return <Tag {...props} />;
  }
};

export const CategoryMarqueeSection: React.FC<CategoryMarqueeSectionProps> = ({
  config,
  darkMode = true,
  onSelectCategory
}) => {
  if (!config || config.enabled === false) {
    return null;
  }

  // Filter visible badges sorted by sortOrder
  const visibleBadges = (config.badges || [])
    .filter(b => b.isVisible !== false)
    .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

  if (visibleBadges.length === 0) {
    return null;
  }

  // Duplicate badge items 3 times for a seamless infinite loop
  const marqueeItems = [...visibleBadges, ...visibleBadges, ...visibleBadges];

  const baseDuration = config.speedDurationSeconds || 30;
  const durationSeconds = Math.round(baseDuration * 1.8);

  return (
    <section className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 my-1 bg-transparent pointer-events-auto">
      {/* Dynamic Keyframes for smooth 60fps GPU-accelerated marquee */}
      <style>{`
        @keyframes marqueeScrollRightToLeft {
          0% {
            transform: translate3d(0, 0, 0);
          }
          100% {
            transform: translate3d(-33.333333%, 0, 0);
          }
        }
        .animate-marquee-infinite {
          display: flex;
          width: max-content;
          will-change: transform;
          animation: marqueeScrollRightToLeft ${durationSeconds}s linear infinite;
        }
        .animate-marquee-infinite:hover {
          ${config.pauseOnHover !== false ? 'animation-play-state: paused;' : ''}
        }
      `}</style>

      {/* Marquee Wrapper with Smooth Fade Edges */}
      <div className="relative w-full group overflow-hidden py-1.5 px-1">
        {/* Left Side Edge Fade */}
        <div className={`absolute left-0 top-0 bottom-0 w-16 sm:w-28 bg-gradient-to-r ${
          darkMode ? 'from-[#0b0f19] via-[#0b0f19]/90' : 'from-slate-50 via-slate-50/90'
        } to-transparent z-20 pointer-events-none`} />
        
        {/* Right Side Edge Fade */}
        <div className={`absolute right-0 top-0 bottom-0 w-16 sm:w-28 bg-gradient-to-l ${
          darkMode ? 'from-[#0b0f19] via-[#0b0f19]/90' : 'from-slate-50 via-slate-50/90'
        } to-transparent z-20 pointer-events-none`} />

        {/* Scrolling Ticker Track */}
        <div className="animate-marquee-infinite items-center gap-[10px] px-2">
          {marqueeItems.map((badge, index) => {
            const glowColor = badge.glowColor || '#38bdf8';
            return (
              <button
                key={`${badge.id}-${index}`}
                onClick={() => {
                  if (onSelectCategory) {
                    onSelectCategory(badge.title);
                  }
                }}
                className="group/pill relative flex-shrink-0 h-[40px] flex items-center gap-2 px-[16px] py-0 rounded-[20px] bg-slate-900/90 border border-slate-800/90 hover:border-cyan-400/80 hover:bg-slate-800/90 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] shadow-sm hover:shadow-[0_0_12px_rgba(56,189,248,0.2)] cursor-pointer text-left"
                style={{
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.25)',
                }}
              >
                {/* Glowing Dot Accent */}
                <span
                  className="w-1.5 h-1.5 rounded-full animate-pulse flex-shrink-0"
                  style={{
                    backgroundColor: glowColor,
                    boxShadow: `0 0 6px ${glowColor}`,
                  }}
                />

                {/* Badge Icon */}
                <div className="flex items-center justify-center p-1 rounded-md bg-slate-950/95 border border-white/5">
                  {renderBadgeIcon(badge.iconName, glowColor)}
                </div>

                {/* Badge Title */}
                <span className="text-[13px] font-medium text-slate-200 group-hover/pill:text-white whitespace-nowrap tracking-wide leading-none">
                  {badge.title}
                </span>

                {/* Hover indicator arrow */}
                <ChevronRight className="w-3 h-3 text-slate-500 opacity-0 group-hover/pill:opacity-100 group-hover/pill:translate-x-0.5 transition-all text-cyan-400" />
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};
